import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { noop } from 'lodash-es';
import { Page, TableList, PageEmptyState, PageErrorState, Button, PageContentHeader, FilterPickerGroup } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { ClientDetailPage } from '~/pages/ClientDetailPage/ClientDetailPage';
import { ClientRow } from './ClientRow/ClientRow';
import { ALL_CLIENT_TYPES, filterClients, sortClients, toClient } from './ClientsPage.utils';
export const clientsPageTextsPath = 'pages.clientsDashboard';
export const GetClientsPageTID = (parentTestId)=>getTestIds(parentTestId);
const sortIds = [
    'clientName',
    'status',
    'type',
    'contacts'
];
const columnDefs = [
    {
        width: [
            140,
            360
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_name'
        }
    },
    {
        width: [
            100,
            240
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_status'
        }
    },
    {
        width: [
            100,
            240
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_type'
        }
    },
    {
        width: [
            160,
            460
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_clients_by_contacts'
        }
    }
];
function ClientsListView(param) {
    let { clients } = param;
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    const history = useHistory();
    const openCreateClient = useOpenCreateClientModal();
    const openEditClient = useOpenEditClientModal();
    const handleViewClient = useCallback((client)=>history.push(routes.client(client.id)), [
        history
    ]);
    const handleEditClient = useCallback((client)=>openEditClient({
            clientId: client.id,
            initialName: client.name,
            initialClientType: client.type
        }), [
        openEditClient
    ]);
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { t: columnHeaderTexts } = useLocaleKeys('pages.clientsDashboard.columnHeaders');
    const { t: statusFilterTexts } = useLocaleKeys('pages.clientsDashboard.statusFilter');
    const { t: typeFilterTexts } = useLocaleKeys('pages.clientsDashboard.typeFilter');
    const { t: emptyStateTexts } = useLocaleKeys('pages.clientsDashboard.emptyState');
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const { urlState: { search, status: statusFilterValue, type: typeFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                'active'
            ],
            multi: true
        },
        type: {
            defaultValue: ALL_CLIENT_TYPES,
            multi: true
        }
    });
    const statusFilterOptions = useMemo(()=>[
            {
                value: 'active',
                label: statusFilterTexts.active()
            },
            {
                value: 'archived',
                label: statusFilterTexts.archived()
            }
        ], []);
    const typeFilterOptions = useMemo(()=>[
            {
                value: 'business',
                label: typeFilterTexts.business()
            },
            {
                value: 'individual',
                label: typeFilterTexts.individual()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label(),
            type: typeFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: statusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const statuses = value;
                        return !Array.isArray(statuses) || statuses.length === 0 || statuses.length === statusFilterOptions.length;
                    }
                }
            },
            type: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: typeFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const types = value;
                        return !Array.isArray(types) || types.length === 0 || types.length === typeFilterOptions.length;
                    }
                }
            }
        }), [
        statusFilterOptions,
        typeFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: statusFilterValue,
            type: typeFilterValue
        }), [
        statusFilterValue,
        typeFilterValue
    ]);
    const headerItems = useMemo(()=>[
            {
                id: 'clientName',
                value: columnHeaderTexts.clientName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'type',
                value: columnHeaderTexts.type()
            },
            {
                id: 'contacts',
                value: columnHeaderTexts.contacts()
            }
        ], []);
    const sortOptions = useMemo(()=>[
            {
                id: 'clientName',
                label: columnHeaderTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnHeaderTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'type',
                label: columnHeaderTexts.type(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contacts',
                label: columnHeaderTexts.contacts(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], []);
    const filteredAndSorted = useMemo(()=>{
        var _sortIds_colIndex;
        const filtered = filterClients({
            clients,
            searchTerm: search,
            statusFilter: statusFilterValue,
            typeFilter: typeFilterValue
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortKey = (_sortIds_colIndex = sortIds[colIndex]) !== null && _sortIds_colIndex !== void 0 ? _sortIds_colIndex : sortIds[0];
        const sortDirection = sort === 'asc' ? 'desc' : 'asc';
        return sortClients({
            clients: filtered,
            sortKey,
            sortDirection
        });
    }, [
        clients,
        search,
        statusFilterValue,
        typeFilterValue,
        tableSort
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef
    });
    const onSortChange = (sort)=>{
        changeSort(sort);
    };
    const handleTableFiltersRefresh = (filters, sort)=>{
        const selectedStatus = filters.status;
        const newStatus = (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : statusFilterOptions.map((opt)=>opt.value);
        const selectedType = filters.type;
        const newType = (selectedType === null || selectedType === void 0 ? void 0 : selectedType.length) ? selectedType : ALL_CLIENT_TYPES;
        changeUrlState({
            status: newStatus,
            type: newType
        });
        if (sort !== undefined) {
            changeSort(sort);
        }
    };
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: statusFilterOptions.map((opt)=>opt.value),
            type: ALL_CLIENT_TYPES
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: TID.Root(),
        ref: scrollElRef,
        header: clientsPageTexts.title(),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'create_client'
            },
            testId: TID.CreateButton(),
            onClick: openCreateClient,
            children: clientsPageTexts.createClient()
        }),
        onScroll: onScroll,
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: tableSort,
            onSortChange: onSortChange,
            numRows: filteredAndSorted.length,
            children: [
                /*#__PURE__*/ _jsxs(PageContentHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(FilterPickerGroup, {
                            style: {
                                marginBottom: 24
                            },
                            children: /*#__PURE__*/ _jsx(TableFilters, {
                                testIdPrefix: TID.Filters(),
                                filterLabels: filterLabels,
                                filterConfig: filterConfig,
                                selectedFilters: selectedFilters,
                                setSelectedFilters: noop,
                                onRefresh: handleTableFiltersRefresh,
                                search: {
                                    bi: {
                                        name: 'clients_search'
                                    },
                                    value: search,
                                    testId: TID.Search(),
                                    onChange: (event)=>{
                                        changeUrlState({
                                            search: event.target.value
                                        });
                                    },
                                    onClear: ()=>changeUrlState({
                                            search: ''
                                        })
                                },
                                mobile: {
                                    sortOptions
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            testId: TID.TableHeader()
                        })
                    ]
                }),
                filteredAndSorted.length ? paginatedData.map((client)=>/*#__PURE__*/ _jsx(ClientRow, {
                        client: client,
                        onEditClient: handleEditClient,
                        onViewClient: handleViewClient
                    }, client.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                    illustration: /*#__PURE__*/ _jsx(_Fragment, {}),
                    title: emptyStateTexts.title(),
                    message: emptyStateTexts.description(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_clients_filters'
                        },
                        testId: TID.ClearFiltersButton(),
                        onClick: onClearFilters,
                        children: emptyStateTexts.clearFilters()
                    })
                })
            ]
        })
    });
}
export function ClientsPage() {
    const { clients: clientModels, isLoading, error, refresh } = useClients({
        refreshOnHookMount: true
    });
    const clients = useMemo(()=>clientModels.map(toClient), [
        clientModels
    ]);
    const detailMatch = useRouteMatch({
        path: routes.client(),
        exact: false
    });
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: clientsPageTexts.title()
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorState(),
            title: clientsPageTexts.title(),
            onRefresh: refresh
        });
    }
    if (detailMatch != null) {
        return /*#__PURE__*/ _jsx(ClientDetailPage, {
            clients: clients,
            match: detailMatch
        });
    }
    return /*#__PURE__*/ _jsx(ClientsListView, {
        clients: clients
    });
}
